import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { resetHeightChannelOverview } from '../../functions/ResetHeightChannelOverview';
import translations from "../../../text/get-started-translations.json";
import language from '../../../utils/LanguageSettings';
import { SOURCE_URL } from '../../../config';
import notFoundWbgE from "../../../images/icon/notfound_vim_wbg_e.png"

export const ChannelButton: React.FC<{ items: any[]; channelSelectedGenre: string; librarySelectedGenre: string; selectedCategory: string }> =
  React.memo(({ items, channelSelectedGenre, librarySelectedGenre, selectedCategory }) => {
    const [openedItem, setOpenedItem] = useState<string | null>(null);
    const [width, setWidth] = useState(window.innerWidth);
    
    const handleToggle = (itemId: string) => {
      setOpenedItem(openedItem === itemId ? null : itemId);
    };

    useEffect(() => {
      setOpenedItem(null);
    }, [channelSelectedGenre, librarySelectedGenre, selectedCategory]);

    const filteredItems = useMemo(() => {
      return items.filter((item) => {
        const selectedGenre = selectedCategory === "on-demand" ? librarySelectedGenre : channelSelectedGenre;
        const matchesGenre = selectedGenre === "all" || item.attributes.ChannelOverview?.Genre?.includes(selectedGenre);
        const matchesCategory = selectedCategory === "channel" ? item.source === "channels" : item.source === "libraries";
        return matchesGenre && matchesCategory;
      });
    }, [items, channelSelectedGenre, librarySelectedGenre, selectedCategory]);


    const rows = useMemo(
      () => groupItemsIntoRows(filteredItems),
      [filteredItems, window.innerWidth]
    );

    useEffect(() => {
      function changeWidth() {
        setWidth(window.innerWidth);
      }
      window.addEventListener('resize', changeWidth);
      changeWidth();

    }, []);

    useEffect(() => {
      resetHeightChannelOverview();
    });
    return (
      <div className="accordion" id="accordionOverview">
        {rows.map((row, rowIndex) => (
          <React.Fragment key={rowIndex}>
            <div className="container">
              <div className="row">
                {row.map(
                  (item) =>
                    item.attributes && (
                      <div key={item.attributes.number} className="item">
                        <ChannelButtonItem
                          item={item}
                          isActive={openedItem === item.attributes.number}
                          onToggle={handleToggle}
                        />
                      </div>
                    )
                )}
              </div>
            </div>
            <div className="container-fluid">
              <div className="row">
                {row.map(
                  (item) =>
                    item.attributes && (
                      <div key={item.attributes.number} className="col-12">
                        <ChannelAccordionItem
                          item={item}
                          isActive={openedItem === item.attributes.number}
                        />
                      </div>
                    )
                )}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  });

const ChannelButtonItem: React.FC<{
  item: any;
  isActive: boolean;
  onToggle: (itemId: string) => void;
}> = ({ item, isActive, onToggle }) => {
  const pageTranslations = translations[language];
  const isLibrary = item.source === "libraries";
  const iconUrl = item.attributes.icon.data
    ? SOURCE_URL + item.attributes.icon.data.attributes.url
    : `${notFoundWbgE}`
  return (
    <button
      className={`channel ${isActive ? "active" : ""}`}
      data-id={item.attributes.number}
      data-category={`category-${item.attributes.number}`}
      data-name={item.attributes.name}
      data-desc={item.attributes.description}
      data-link={`https://nowplayer.now.com/tvguide/channeldetail/${item.attributes.number}/1?isfromchannel=false`}
      data-label="Channel Schedule"
      data-bs-toggle="collapse"
      type="button"
      data-bs-target={`#collapse${item.attributes.number}`}
      aria-expanded={isActive ? "true" : "false"}
      aria-controls={`collapse${item.attributes.number}`}
      onClick={() => onToggle(item.attributes.number)}
    >
      <div className={`channel-content${isActive ? " active" : ""}`}>
        <span className="text-16">
          {isLibrary ? item.attributes.name : language === "en" ? `${pageTranslations.CH} ${item.attributes.number}` : `${item.attributes.number} ${pageTranslations.CH}`}
        </span>
        <div className="channel-logo">
          <img
            data-url={iconUrl}
            className="img-fluid lazy apply-default"
            alt=""
            data-default={notFoundWbgE}
            src={iconUrl}
            onError={(e) => {
              e.currentTarget.src = `${notFoundWbgE}`
            }}
          />
        </div>
      </div>
    </button>
  );
};

const ChannelAccordionItem: React.FC<{ item: any; isActive: boolean }> = ({
  item,
  isActive,
}) => {
  const itemId = item?.attributes?.number?.replace(/\s+/g, "-") || ""; 
  const channelOverview = item.attributes.ChannelOverview;
  const linkUrl = channelOverview?.URL;
  const linkLabel = linkUrl ? channelOverview?.Label || (item.source === "channels" ? translations[language].ChannelSchedule : translations[language].WatchonNowTVAppWebsite) : '';

  return (
    <div
      className={`accordion-item channel_description ${isActive ? "active" : ""
        }`}
    >
      <div
        id={`collapse${itemId}`}
        className={`accordion-collapse collapse ${isActive ? "show" : ""
          }`}
        aria-labelledby={`heading${itemId}`}
        data-bs-parent="#accordionOverview"
      >
        <div className="accordion-body">
          <div className="inner-wrap container px-4">
            <div className="wrapper">
              <div className="title">
                <h3 className="text-20 title">{item.attributes.name}</h3>
                <Link
                  to={linkUrl}
                  className="text-16 nowplayer_link barlow-font color-4"
                  target="_blank"
                >
                  {linkLabel}
                </Link>
              </div>
              <div className="description">
                <p className="text-16 channel_desc">
                  {item.attributes.description || "\u00A0"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const groupItemsIntoRows = (items: any[]) => {
  const rows = [];
  const itemsPerRowDynamic = getItemsPerRow();
  for (let i = 0; i < items.length; i += itemsPerRowDynamic) {
    rows.push(items.slice(i, i + itemsPerRowDynamic));
  }
  return rows;
};

const getItemsPerRow = () => {
  if (window.innerWidth >= 1200) {
    return 6;
  } else if (window.innerWidth >= 992) {
    return 5;
  } else if (window.innerWidth >= 768) {
    return 4;
  } else if (window.innerWidth >= 576) {
    return 3;
  } else {
    return 2;
  }
};
